import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import useAuthStore from '../stores/useAuthStore';
import useCourseStore from '../stores/useCourseStore';
import axiosClient from '../services/api';
import { useToast } from '../components/ToastContainer';

const InstructorCourseDashboard = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuthStore();
  const {
    enrolledCourses: instructorCourses,
    fetchMyCourses,
    isLoading,
    error
  } = useCourseStore();

  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    if (user?.role === 'instructor') {
      fetchMyCourses();
    }
  }, [user?.role, fetchMyCourses]);

  const filteredCourses = instructorCourses.filter(course => {
    if (activeTab === 'all') return true;
    return course.status === activeTab;
  });

  const togglePartnerProgram = async (courseId) => {
    try {
      await axiosClient.put(`/partners/courses/${courseId}/toggle`);
      toast.info('partner course list updated successfuly')
      fetchMyCourses();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'rejected':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      case 'draft':
        return 'bg-neutral-100 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-300';
      default:
        return 'bg-neutral-100 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-300';
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout userRole={user.role}>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userRole={user.role}>
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-neutral-900 dark:text-white">My Courses</h1>
            <p className="text-neutral-600 dark:text-neutral-400 mt-2">
              Manage your courses, track enrollments, and monitor performance
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}

          {/* Status Tabs */}
          <div className="mb-6 border-b border-neutral-200 dark:border-neutral-700 overflow-x-auto hide-scrollbar">
            <nav className="flex space-x-6 min-w-max pb-4">
              {['all', 'draft', 'pending', 'approved', 'rejected'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm capitalize ${activeTab === tab
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
                    }`}
                >
                  {tab === 'all' ? 'All Courses' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </nav>
          </div>

          {filteredCourses.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredCourses.map((course) => (
                <div
                  key={course._id}
                  className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 overflow-hidden"
                >
                  <div className="p-5">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold text-neutral-900 dark:text-white line-clamp-2">
                          {course.title}
                        </h3>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
                          {course?.category?.name}
                        </p>
                      </div>
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(course.status)}`}>
                        {course.status.charAt(0).toUpperCase() + course.status.slice(1)}
                      </span>
                    </div>

                    {/* Stats */}
                    <div className="mt-4 grid grid-cols-2 gap-3">
                      <div className="p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
                        <p className="text-xs text-neutral-500 dark:text-neutral-400">Enrollments</p>
                        <p className="text-sm font-medium text-neutral-900 dark:text-white">
                          {course.totalEnrollments || 0}
                        </p>
                      </div>
                      <div className="p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
                        <p className="text-xs text-neutral-500 dark:text-neutral-400">Revenue</p>
                        <p className="text-sm font-medium text-neutral-900 dark:text-white">
                          ₦{(course.totalRevenue || 0).toLocaleString()}
                        </p>
                      </div>
                    </div>

                    {course.status === 'rejected' && course.rejectionReason && (
                      <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                        <p className="text-xs text-red-700 dark:text-red-300">
                          <strong>Rejection Reason:</strong> {course.rejectionReason}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="px-5 py-4 bg-neutral-50 dark:bg-neutral-700/50 border-t border-neutral-200 dark:border-neutral-700 flex space-x-3">
                    <button
                      onClick={() => navigate(`/instructor/course/edit-course/${course._id}`)}
                      className="flex-1 px-3 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => navigate(`/instructor/analytics`)}
                      className="flex-1 px-3 py-2 bg-secondary-500 hover:bg-secondary-600 text-white text-sm font-medium bg-gray-500 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-secondary-500"
                    >
                      Analytics
                    </button>
                  </div>
                  {user.isPartner && (
                    <button
                      onClick={() => togglePartnerProgram(course._id)}
                      className={`px-3 py-1 m-2 rounded block text-sm  underline ${course.isInPartnerProgram
                        ? ' text-green-800 dark:text-green-400'
                        : ' text-neutral-800 dark:text-neutral-200'
                        }`}
                    >
                      {course?.isInPartnerProgram ? 'In Partner Program' : 'Add to Partner Program'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">👩‍🏫</div>
              <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">
                No courses found
              </h3>
              <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                {activeTab === 'all'
                  ? 'Create your first course to start teaching'
                  : `No courses with status "${activeTab}"`}
              </p>
              {activeTab === 'all' && (
                <button
                  onClick={() => navigate('/instructor/course/builder')}
                  className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  Create Course
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default InstructorCourseDashboard;